import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'
import {
  Wrench,
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  Save,
  X,
  Phone,
  MapPin,
} from 'lucide-react'

interface Service {
  id: string
  name: string
  category: string
  phone: string
  address: string | null
  description: string | null
  is_active?: boolean | null
  created_at: string
  updated_at: string
}

const categoryOptions = [
  'Plumbing', 'Electrical', 'Carpentry', 'Healthcare', 'Retail', 'General',
  'Transportation', 'Education', 'Agriculture', 'Construction', 'Maintenance'
]

export function ServicesManager() {
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [bulkSaving, setBulkSaving] = useState(false)
  const [error, setError] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingService, setEditingService] = useState<Service | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    category: 'General',
    phone: '',
    address: '',
    description: '',
    is_active: true,
  })

  useEffect(() => {
    fetchServices()
  }, [])

  const fetchServices = async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setServices(data || [])
    } catch (err) {
      setError('Failed to fetch services')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')

    try {
      if (editingService) {
        // Update existing
        const { error } = await supabase
          .from('services')
          .update({
            ...formData,
            address: formData.address || null,
            description: formData.description || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingService.id)

        if (error) throw error
      } else {
        // Create new
        const { error } = await supabase
          .from('services')
          .insert({
            ...formData,
            address: formData.address || null,
            description: formData.description || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })

        if (error) throw error
      }

      await fetchServices()
      handleCloseDialog()
    } catch (err) {
      setError('Failed to save service')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id)

      if (error) throw error
      await fetchServices()
    } catch (err) {
      setError('Failed to delete service')
    }
  }

  const handleToggleActive = async (service: Service, checked: boolean) => {
    setError('')
    try {
      const { error } = await supabase
        .from('services')
        .update({
          is_active: checked,
          updated_at: new Date().toISOString(),
        })
        .eq('id', service.id)

      if (error) throw error
      await fetchServices()
    } catch (err) {
      setError('Failed to update service status')
    }
  }

  const handleSetAllActive = async (active: boolean) => {
    const ids = services.map((s) => s.id)
    if (ids.length === 0) return

    setBulkSaving(true)
    setError('')
    try {
      const { error } = await supabase
        .from('services')
        .update({
          is_active: active,
          updated_at: new Date().toISOString(),
        })
        .in('id', ids)

      if (error) throw error
      await fetchServices()
    } catch (err) {
      setError('Failed to update all services')
    } finally {
      setBulkSaving(false)
    }
  }

  const handleEdit = (service: Service) => {
    setEditingService(service)
    setFormData({
      name: service.name,
      category: service.category,
      phone: service.phone,
      address: service.address || '',
      description: service.description || '',
      is_active: service.is_active !== false,
    })
    setIsDialogOpen(true)
  }

  const handleAddNew = () => {
    setEditingService(null)
    setFormData({
      name: '',
      category: 'General',
      phone: '',
      address: '',
      description: '',
      is_active: true,
    })
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingService(null)
    setFormData({
      name: '',
      category: 'General',
      phone: '',
      address: '',
      description: '',
      is_active: true,
    })
  }

  const allServicesEnabled =
    services.length > 0 && services.every((s) => s.is_active !== false)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading services...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
            Services Management
          </h2>
          <p className="text-slate-600 text-lg">
            Manage essential services and local businesses
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full md:w-auto">
          <div className="inline-flex w-fit max-w-full items-center gap-2 rounded-lg border border-input bg-muted/30 px-2.5 py-1.5">
            <Label
              htmlFor="bulk-all-active"
              className="text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              {bulkSaving ? 'Updating…' : 'All on portal'}
            </Label>
            <Switch
              id="bulk-all-active"
              checked={allServicesEnabled}
              disabled={bulkSaving || services.length === 0}
              onCheckedChange={(checked) => void handleSetAllActive(checked)}
              aria-label="Enable or disable all services on the public portal"
            />
          </div>
          <Button
            onClick={handleAddNew}
            className="w-full sm:w-auto bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Service
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Services ({services.length})
          </CardTitle>
          <CardDescription>
            Essential services and local businesses in the village
          </CardDescription>
        </CardHeader>
        <CardContent>
          {services.length === 0 ? (
            <div className="text-center py-8">
              <Wrench className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No services found</p>
              <Button onClick={handleAddNew} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Add First Service
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{service.name}</div>
                        {service.description && (
                          <div className="text-sm text-muted-foreground">
                            {service.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{service.category}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                        <Badge
                          className={
                            service.is_active !== false
                              ? 'bg-gradient-to-r from-orange-600 to-amber-600 border-0'
                              : undefined
                          }
                          variant={service.is_active !== false ? 'default' : 'secondary'}
                        >
                          {service.is_active !== false ? 'Enabled' : 'Disabled'}
                        </Badge>
                        <Switch
                          checked={service.is_active !== false}
                          onCheckedChange={(checked) => handleToggleActive(service, checked)}
                          aria-label={service.is_active !== false ? 'Disable service' : 'Enable service'}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Phone className="w-3 h-3" />
                        {service.phone}
                      </div>
                    </TableCell>
                    <TableCell>
                      {service.address ? (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="w-3 h-3" />
                          {service.address}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">No address</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(service)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete service?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone. The selected service will be removed from the portal.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
                                onClick={() => handleDelete(service.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingService ? 'Edit Service' : 'Add New Service'}
            </DialogTitle>
            <DialogDescription>
              {editingService ? 'Update service information' : 'Add a new essential service'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Service Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Ravi Plumbing Services"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md"
                >
                  {categoryOptions.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-3 rounded-lg border border-input bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <Label htmlFor="is_active" className="text-base">
                  Show on public portal
                </Label>
                <p className="text-sm text-muted-foreground">
                  Turn off to hide this listing from the village home page.
                </p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(is_active) => setFormData({ ...formData, is_active })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+91 98765 43210"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Near Bus Stand, Village Name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the service offered..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleCloseDialog}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
